import { useState, useCallback, useEffect, useMemo } from 'react'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import {
  getTemporaryUploadUrl,
  uploadVideoToAzure,
} from '@/services/renters.services'
import { useAuth } from '@/shared/hooks/use-auth'
import { LOCAL_STORAGE_KEY } from '@/shared/constants/storage'

type StoredVideo = { fileName: string; id: number }

function getStorageKey(userId: string) {
  return `${LOCAL_STORAGE_KEY.RENTERS_VIDEO}_${userId}`
}

function readStoredVideo(userId: string): StoredVideo | undefined {
  try {
    const raw = localStorage.getItem(getStorageKey(userId))
    return raw ? (JSON.parse(raw) as StoredVideo) : undefined
  } catch {
    return undefined
  }
}

function writeStoredVideo(userId: string, data: StoredVideo) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(data))
}

interface UseRentersFormTwoProps {
  onContinue: () => void
  onRegisterSubmit?: (fn: () => Promise<void>) => void
  onLoadingChange?: (loading: boolean) => void
}

export function useRentersFormTwo({
  onContinue,
  onRegisterSubmit,
  onLoadingChange,
}: UseRentersFormTwoProps) {
  const { user } = useAuth()
  const userId = user?.ciUserId ?? user?.id ?? ''

  const [videoFile, setVideoFile] = useState<File | undefined>(undefined)
  const [storedVideo, setStoredVideo] = useState<StoredVideo | undefined>(
    undefined,
  )
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(false)

  useEffect(() => {
    if (!userId) return
    setStoredVideo(readStoredVideo(userId))
  }, [userId])

  const handleFileSelect = useCallback((file: File) => {
    setVideoFile(file)
    setUploadError(false)
  }, [])

  const handleContinue = useCallback(async () => {
    if (!videoFile) {
      onContinue()
      return
    }
    setIsUploading(true)
    setUploadError(false)
    try {
      const jwt = await getJwt()
      if (!jwt) throw new Error('No auth token')
      const extension = videoFile.name.split('.').pop() ?? 'mp4'
      const { id, url, blobType } = await getTemporaryUploadUrl(jwt, extension)
      await uploadVideoToAzure(url, blobType, videoFile)
      const stored: StoredVideo = { fileName: videoFile.name, id }
      if (userId) writeStoredVideo(userId, stored)
      setStoredVideo(stored)
      onContinue()
    } catch {
      setUploadError(true)
    } finally {
      setIsUploading(false)
    }
  }, [videoFile, userId, onContinue])

  useEffect(() => {
    onRegisterSubmit?.(handleContinue)
  }, [onRegisterSubmit, handleContinue])

  useEffect(() => {
    onLoadingChange?.(isUploading)
  }, [onLoadingChange, isUploading])

  // Preview only available in current session (write URL from UploadUrl is not for reading)
  const previewUrl = useMemo(
    () => (videoFile ? URL.createObjectURL(videoFile) : undefined),
    [videoFile],
  )
  const displayFileName = videoFile?.name ?? storedVideo?.fileName

  return {
    displayFileName,
    previewUrl,
    handleFileSelect,
    isUploading,
    uploadError,
  }
}
